import type { EmployeePersonalInformationDto } from "@/generated/model/employeePersonalInformationDto";
import { useUpdateEmployeePersonalInformation } from "@/generated/people/people";
import {
  useProfileSectionForm,
  useProfileSectionSave,
} from "@/hooks/use-profile-section-form";
import {
  personalInformationFormValues,
  personalInformationRequest,
  personalInformationSchema,
} from "@/lib/people-profile-sections";

import { ProfileSectionForm } from "../ProfileSectionForm";
import {
  SectionDateField,
  SectionRadioGroup,
  SectionTextField,
  YES_NO_OPTIONS,
} from "./ProfileSectionFields";

interface EditPersonalInformationFormProps {
  nurseryId: string;
  userId: string;
  personalInformation: EmployeePersonalInformationDto;
  onDone: () => void;
}

export function EditPersonalInformationForm({
  nurseryId,
  userId,
  personalInformation,
  onDone,
}: EditPersonalInformationFormProps) {
  const { values, setErrors, setField, errors, validate } =
    useProfileSectionForm(
      () => personalInformationFormValues(personalInformation),
      personalInformationSchema,
    );
  const update = useUpdateEmployeePersonalInformation({
    mutation: useProfileSectionSave(
      nurseryId,
      userId,
      "Personal information",
      onDone,
      setErrors,
    ),
  });
  const isPending = update.isPending;

  const handleSubmit = () => {
    if (!validate()) return;
    update.mutate({
      nurseryId,
      userId,
      data: personalInformationRequest(values),
    });
  };

  return (
    <ProfileSectionForm
      testId="edit-personal-information-form"
      onCancel={onDone}
      isPending={isPending}
      onSubmit={handleSubmit}
    >
      <SectionDateField
        label="Date of birth"
        name="dateOfBirth"
        value={values.dateOfBirth}
        onChange={(value) => setField("dateOfBirth", value)}
        disabled={isPending}
        error={errors.dateOfBirth}
      />
      <SectionTextField
        label="Nationality"
        name="nationality"
        value={values.nationality}
        onChange={(value) => setField("nationality", value)}
        disabled={isPending}
        error={errors.nationality}
      />
      <SectionTextField
        label="National Insurance number"
        name="nationalInsuranceNumber"
        value={values.nationalInsuranceNumber}
        onChange={(value) => setField("nationalInsuranceNumber", value)}
        disabled={isPending}
        error={errors.nationalInsuranceNumber}
        helpText="For example AB 12 34 56 C"
      />
      <SectionTextField
        label="Place of birth"
        name="placeOfBirth"
        value={values.placeOfBirth}
        onChange={(value) => setField("placeOfBirth", value)}
        disabled={isPending}
        error={errors.placeOfBirth}
      />
      <SectionTextField
        label="Preferred language"
        name="preferredLanguage"
        value={values.preferredLanguage}
        onChange={(value) => setField("preferredLanguage", value)}
        disabled={isPending}
        error={errors.preferredLanguage}
      />
      <SectionRadioGroup
        label="Driving licence holder"
        value={values.drivingLicenceHolder}
        options={YES_NO_OPTIONS}
        onChange={(value) => setField("drivingLicenceHolder", value)}
        disabled={isPending}
      />
      <SectionDateField
        label="DBS check enforcement date"
        name="dbsCheckEnforcementDate"
        value={values.dbsCheckEnforcementDate}
        onChange={(value) => setField("dbsCheckEnforcementDate", value)}
        disabled={isPending}
        error={errors.dbsCheckEnforcementDate}
      />
    </ProfileSectionForm>
  );
}
